// src/pages/services/VisaAssistance.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaPassport,
  FaClipboardList,
  FaFileAlt,
  FaUserCheck,
  FaStamp,
  FaShieldAlt,
  FaClock,
  FaQuestionCircle,
  FaUniversity,
  FaMoneyBillWave,
  FaHeartbeat,
  FaIdCard,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

const visaTypes = [
  {
    title: "Student Visa (Subclass 500)",
    desc: "Full-time study at Australian universities, colleges, and schools.",
    path: "/visa/student-visa-500",
  },
  {
    title: "Post-Study Work Visa (Subclass 485)",
    desc: "Gain Australian work experience after completing eligible study.",
    path: "/visa/post-study-work-visa-485",
  },
  {
    title: "Tourist / Visitor Visa (Subclass 600)",
    desc: "Visit for holidays, family trips, or short business activities.",
    path: "/visa/tourist-visa-600",
  },
  {
    title: "Skilled Migration (189 / 190 / 491)",
    desc: "Points-tested pathways invited by Commonwealth or a State.",
    path: "/contact",
  },
  {
    title: "Partner Visa / Australian Citizenship",
    desc: "For spouses/partners or dependents of student/skilled visa holders.",
    path: "/contact",
  },
];

const steps = [
  {
    icon: <FaFileAlt size={20} />,
    title: "Step 1: Document Prep",
    desc:
      "Tailored checklist + organise COEs, GS/GTE, financials, OSHC, identity, PCC/health (if needed).",
  },
  {
    icon: <FaUserCheck size={20} />,
    title: "Step 2: Forms & Review",
    desc:
      "ImmiAccount setup, forms, uploads, and a detailed pre-lodgement quality review.",
  },
  {
    icon: <FaStamp size={20} />,
    title: "Step 3: Lodge & Track",
    desc:
      "We lodge a complete file, track updates, and guide health checks, biometrics, and next steps.",
  },
];

const docs = [
  { icon: <FaUniversity />, title: "COE / Offer", text: "Valid COE(s) or offer/acceptance per case." },
  { icon: <FaClipboardList />, title: "GS / GTE", text: "Clear, consistent Genuine Student statement." },
  { icon: <FaMoneyBillWave />, title: "Funds Evidence", text: "If required: tuition, living, travel proof." },
  { icon: <FaHeartbeat />, title: "OSHC", text: "Student OSHC aligned to course dates." },
  { icon: <FaIdCard />, title: "Identity", text: "Passport, photos, previous visas, PCC/health (if requested)." },
];

const faqs = [
  {
    q: "How long does a student visa take to process?",
    a:
      "Timelines vary by policy settings and case complexity. We’ll set realistic prep + lodgement windows and keep you updated.",
  },
  {
    q: "What is GS/GTE and do I need it?",
    a:
      "Most student cases require a Genuine Student statement. We workshop your draft and align it with your documents.",
  },
  {
    q: "Do I always need to show funds?",
    a:
      "It depends on your risk profile and education provider. We’ll confirm if funds evidence applies and how to present it.",
  },
];

export default function VisaAssistance() {
  const [checkGS, setCheckGS] = useState(false);
  const [checkFunds, setCheckFunds] = useState(false);
  const [checkOSHC, setCheckOSHC] = useState(false);

  const readyCount = [checkGS, checkFunds, checkOSHC].filter(Boolean).length;

  return (
    <div className="relative min-h-screen bg-white">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/30 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-200/25 blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-white/60 to-blue-50/40">
        <div className="mx-auto max-w-7xl px-6 pt-24 pb-12 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600"
          >
            <FaPassport className="text-2xl" />
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-blue-800"
          >
            Visa Application Support
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mx-auto mt-4 max-w-2xl text-gray-700 text-lg md:text-xl"
          >
            Calm, end-to-end assistance to lodge a strong, compliant application — clear checklist,
            GS/GTE review, forms, uploads, and final submission.
          </motion.p>

          <motion.div
            className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ originX: 0 }}
          />
        </div>
      </section>

      {/* Visa Types (Tilt cards) */}
      <section className="mx-auto max-w-7xl px-6 pb-10">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-6 text-center text-2xl md:text-3xl font-bold text-blue-800"
        >
          Explore Visa Types
        </motion.h2>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visaTypes.map((visa, i) => (
            <motion.div
              key={visa.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
            >
              <Tilt glareEnable glareMaxOpacity={0.15} glareColor="#60a5fa" glarePosition="all">
                <Link
                  to={visa.path}
                  className="group block h-full rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  aria-label={`${visa.title} – Discover More`}
                >
                  <h3 className="text-lg font-semibold text-blue-900 group-hover:text-blue-700 transition-colors">
                    {visa.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">{visa.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-blue-700 font-medium">
                    Discover More <motion.span animate={{ x: [0, 6, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>→</motion.span>
                  </span>
                </Link>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process + Trust strip */}
      <section className="bg-gradient-to-b from-blue-50/60 to-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mb-8 text-center text-2xl md:text-3xl font-bold text-blue-800"
          >
            How It Works
          </motion.h2>

          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
                className="relative rounded-2xl border bg-white p-6 shadow-sm transition hover:shadow-md"
              >
                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  {s.icon}
                </div>
                <h3 className="font-semibold text-blue-800">{s.title}</h3>
                <p className="mt-1 text-gray-700 text-sm leading-relaxed">{s.desc}</p>

                {/* bottom gradient accent */}
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] rounded-b-2xl bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-500 opacity-0 transition-opacity duration-300 hover:opacity-100" />
              </motion.div>
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-xl border bg-white/70 p-5 shadow-sm backdrop-blur"
            >
              <h4 className="flex items-center gap-2 font-semibold text-sky-700">
                <FaShieldAlt className="text-sky-600" /> Quality & Compliance
              </h4>
              <p className="mt-1 text-sm text-gray-700">
                Files checked against current settings and common refusal reasons. Clear & consistent
                evidence = stronger lodgements.
              </p>
            </motion.div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-xl border bg-white/70 p-5 shadow-sm backdrop-blur"
            >
              <h4 className="flex items-center gap-2 font-semibold text-amber-700">
                <FaClock className="text-amber-600" /> Timelines You Can Plan Around
              </h4>
              <p className="mt-1 text-sm text-gray-700">
                Realistic prep windows and on-schedule lodgement to align OSHC, travel, and enrolment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Readiness checker */}
      <section className="mx-auto max-w-7xl px-6 mt-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/80 p-6 shadow-sm backdrop-blur"
        >
          <div className="text-blue-800 font-semibold mb-3 flex items-center gap-2">
            <FaClipboardList /> Quick Readiness Check
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={checkGS} onChange={(e) => setCheckGS(e.target.checked)} />
              I can provide a Genuine Student (GS) / GTE statement
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={checkFunds} onChange={(e) => setCheckFunds(e.target.checked)} />
              I have (or can arrange) funds evidence if required
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={checkOSHC} onChange={(e) => setCheckOSHC(e.target.checked)} />
              I can arrange OSHC aligned to my study dates
            </label>
          </div>

          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm ${
              readyCount >= 2 ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"
            }`}
          >
            {readyCount >= 2
              ? "Great! You look close to ready. We’ll finalise gaps and prepare to lodge."
              : "No stress—start with a quick consult and we’ll map your documents and timeline."}
          </div>
        </motion.div>
      </section>

      {/* Documents checklist (glass cards) */}
      <section className="mx-auto max-w-7xl px-6 mt-8">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-900 font-semibold mb-3"
        >
          Typical Documents We Help With
        </motion.h3>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {docs.map((d, i) => (
            <motion.div
              key={d.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
            >
              <Tilt glareEnable glareMaxOpacity={0.15} glareColor="#60a5fa" glarePosition="all">
                <div className="h-full rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)] transition">
                  <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-200">
                    {d.icon}
                  </div>
                  <h4 className="text-blue-900 font-semibold">{d.title}</h4>
                  <p className="mt-1 text-gray-700 text-sm leading-relaxed">{d.text}</p>
                </div>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="mx-auto max-w-3xl px-6 py-12">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-6 text-center text-2xl md:text-3xl font-bold text-blue-800"
        >
          Frequently Asked Questions
        </motion.h2>

        <div className="space-y-3">
          {faqs.map((f, i) => (
            <Accordion key={f.q} title={f.q} body={f.a} i={i} />
          ))}
        </div>
      </section>

      {/* CTA ribbon */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
        >
          <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Need help with your visa?
                </h3>
                <p className="mt-1 text-gray-600">
                  Book a quick consult — we’ll map your timeline, documents, and next steps.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500"
                >
                  Talk to Our Visa Experts
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

/* Small accordion */
function Accordion({ title, body, i = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, delay: i * 0.02 }}
            className="mt-2 text-gray-700 text-sm leading-relaxed"
          >
            {body}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
